import { useState } from "react";
import { Plus, X } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Deals, DealContacts } from "@/api/endpoints";
import type { ContactCreate, Stage } from "@/api/types";
import { apiErrorMessage } from "@/api/client";
import ContactForm from "@/components/ContactForm";
import AccountPicker from "@/components/AccountPicker";
import AttributionFields, { type Attribution } from "@/components/AttributionFields";
import { toast } from "@/lib/feedback";

interface Props {
  stages: Stage[];
  onClose: () => void;
}

type StagedContact = {
  contact_id?: string;
  contact?: ContactCreate;
  relationship?: string | null;
};

export default function NewDealModal({ stages, onClose }: Props) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    lead: "",
    stage: stages.find((s) => !s.is_terminal)?.name ?? stages[0]?.name ?? "Discovery",
    probability: 0.1,
    monthly_fee: "" as string | number,
    one_time_fee: "" as string | number,
    account_id: null as string | null,
  });
  const [attribution, setAttribution] = useState<Attribution>({
    sourcer_user_id: null,
    sourcer_contact_id: null,
    closer_user_id: null,
  });

  const [staged, setStaged] = useState<StagedContact[]>([]);
  const [showForm, setShowForm] = useState(false);
  // Incrementing key forces a fresh ContactForm each time it's opened, since the
  // form doesn't reset its own fields after submit.
  const [formKey, setFormKey] = useState(0);

  const openForm = () => {
    setFormKey((k) => k + 1);
    setShowForm(true);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const deal = await Deals.create({
        lead: form.lead.trim(),
        stage: form.stage,
        probability: Number(form.probability),
        monthly_fee: form.monthly_fee ? Number(form.monthly_fee) : null,
        one_time_fee: form.one_time_fee ? Number(form.one_time_fee) : null,
        account_id: form.account_id,
        ...attribution,
      });
      // Deal is created — link staged contacts, surfacing per-contact failures
      // without rolling back the deal (the deal is the primary artifact).
      const failures: string[] = [];
      for (const s of staged) {
        try {
          await DealContacts.attach(deal.id, s);
        } catch (e) {
          const label = s.contact?.name ?? "linked contact";
          failures.push(`${label}: ${apiErrorMessage(e)}`);
        }
      }
      return { deal, failures };
    },
    onSuccess: ({ failures }) => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      // A staged contact may have been created inline, so bust the contacts search cache too.
      qc.invalidateQueries({ queryKey: ["contacts"] });
      if (failures.length) {
        toast.err("Deal created, but some contacts could not be linked:\n" + failures.join("\n"));
      }
      onClose();
    },
    onError: (e) => toast.err(`Could not create deal: ${apiErrorMessage(e)}`),
  });

  return (
    <>
      <div className="overlay-backdrop z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(540px,92vw)] panel p-5 max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">New deal</h2>
          <button className="btn btn-ghost" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label">Company / Lead*</label>
            <input
              className="input"
              autoFocus
              value={form.lead}
              onChange={(e) => setForm({ ...form, lead: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Stage</label>
              <select
                className="input"
                value={form.stage}
                onChange={(e) => setForm({ ...form, stage: e.target.value })}
              >
                {stages.map((s) => (
                  <option key={s.id} value={s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Probability (%)</label>
              <input
                type="number"
                step={5}
                min={0}
                max={100}
                className="input"
                value={Math.round(form.probability * 100)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    probability: Math.min(100, Math.max(0, Number(e.target.value))) / 100,
                  })
                }
              />
            </div>
          </div>

          <div>
            <label className="label">Account</label>
            <AccountPicker
              value={form.account_id}
              onChange={(account_id) => setForm({ ...form, account_id })}
            />
          </div>

          <AttributionFields
            value={attribution}
            onChange={(patch) => setAttribution((a) => ({ ...a, ...patch }))}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Monthly fee (€)</label>
              <input
                type="number"
                className="input"
                value={form.monthly_fee}
                onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
              />
            </div>
            <div>
              <label className="label">One-time fee (€)</label>
              <input
                type="number"
                className="input"
                value={form.one_time_fee}
                onChange={(e) => setForm({ ...form, one_time_fee: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <span className="label">Contacts</span>
              {!showForm && (
                <button type="button" className="btn btn-ghost" onClick={openForm}>
                  <Plus className="w-3.5 h-3.5" /> Add contact
                </button>
              )}
            </div>

            {staged.length > 0 && (
              <ul className="space-y-1.5">
                {staged.map((s, idx) => {
                  const name = s.contact?.name ?? "Linked contact";
                  const hint = s.contact
                    ? s.contact.kind === "broker_contact"
                      ? "Broker"
                      : "Account contact"
                    : "Existing contact";
                  return (
                    <li
                      key={idx}
                      className="panel-tight p-2.5 flex items-start justify-between gap-2"
                    >
                      <div className="min-w-0">
                        <div className="text-md font-medium truncate">{name}</div>
                        <div className="text-xs text-mut-500">
                          {hint}
                          {s.relationship ? ` · ${s.relationship}` : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost shrink-0"
                        onClick={() => setStaged(staged.filter((_, i) => i !== idx))}
                        aria-label={`Remove ${name}`}
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {showForm && (
              <ContactForm
                key={formKey}
                onSubmit={(payload) => {
                  setStaged([...staged, payload]);
                  setShowForm(false);
                }}
                onCancel={() => setShowForm(false)}
              />
            )}
          </div>

          <p className="text-xs text-mut-500">
            You'll be set as the primary owner — assign others from the deal's Owners card.
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={!form.lead.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? "Creating…" : "Create deal"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
